import type { Item, TripView } from "./api.ts";
import { useShareLink } from "./Invite.tsx";
import { shareText } from "./inviting.ts";
import { tripPath } from "./router.ts";
import { Icon } from "./souvenir.ts";
import { ItemIcon } from "./ui.tsx";

// Share, in a card's header in full (features/map.md): sends the item's stable link to the others, "to ask
// if they are interested in going". Wherever the browser has a share sheet that always opens (a phone's, and
// since 2026-10-30 a computer's but a Chromium browser's on a Mac: `hasShareSheet` in Invite.tsx), the sheet
// with "teamLab Planets, on our Japan & Korea trip: interested?" and the link; where there's none, or it
// refuses, the link copied, said for a moment. The link is the one AIs and notifications give (src/config.ts,
// tripUrl): it opens the card in full for anyone in the trip, through renames, moves and edits.
//
// `compact`: the phone's, a round icon beside the card's close, "Link copied" floating under it; else the
// computer's, words beside the card's close, with a quiet "Copy link" before Share where Share opens a sheet
// (a Mac's may offer no copy, and a link is often pasted into a chat on the same computer).
export function ShareItem({ item, trip, compact }: { item: Item; trip: TripView; compact: boolean }) {
  const link = `${location.origin}${tripPath(trip.id, item.id, null, { full: true })}`;
  const { share, copy, copied, sheet } = useShareLink(shareText(item.title, trip.title), link);
  if (compact)
    return (
      <span className="tm-share">
        <button type="button" className="tm-layer-close tm-share-icon" aria-label="Share" onClick={share}>
          <Icon name={copied ? "check" : "share"} size={20} />
        </button>
        {copied && (
          <span className="caption tm-share-copied" role="status">
            Link copied
          </span>
        )}
      </span>
    );
  if (!sheet)
    return (
      <button type="button" className="tm-share-words" onClick={share}>
        <Icon name={copied ? "check" : "share"} size={16} />
        <span aria-live="polite">{copied ? "Link copied" : "Share"}</span>
      </button>
    );
  // Share keeps its words: a copy, Copy link's or a refusing sheet's, is said by Copy link.
  return (
    <>
      <button type="button" className="tm-share-words tm-copy-link" onClick={copy}>
        <ItemIcon name={copied ? "check" : "link"} size={16} />
        <span aria-live="polite">{copied ? "Link copied" : "Copy link"}</span>
      </button>
      <button type="button" className="tm-share-words" onClick={share}>
        <Icon name="share" size={16} />
        <span>Share</span>
      </button>
    </>
  );
}
